import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Bookmark, Sparkles, Clock, Moon, Sun, Globe, Menu, X, Hammer, LogIn, LogOut, User, Bell, BarChart3, Settings, Crown, Megaphone, Gem } from 'lucide-react';
import { CountrySelector } from './CountrySelector';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../lib/utils';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { LoginModal } from './LoginModal';
import { hasUnreadNews } from '../lib/newsMeta';
import { SignupNudge } from './SignupNudge';
import { ImageSearchButton } from './ImageSearchButton';
import { getInitialTheme, applyTheme, persistTheme, watchSystemTheme } from '../lib/theme';
import { useHideOnScroll } from '../hooks/useHideOnScroll';
import { useSearchAutocomplete } from '../hooks/useSearchAutocomplete';
import { SearchSuggestions } from './SearchSuggestions';

const LANGUAGES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
];

export default function Header({ searchQuery, onSearch }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, login, logout } = useAuth();
  const { permission, isSubscribed, isSupported, subscribe } = usePushNotifications();
  const [dark, setDark] = useState(() => getInitialTheme() === 'dark');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerSearch, setHeaderSearch] = useState(searchQuery || '');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [newsUnread, setNewsUnread] = useState(false);
  const isHome = ['/de', '/fr', '/it', '/en', '/'].includes(location.pathname);
  // Utility/info pages: hide the sticky mobile search bar (reduces vertical space
  // "stealing" and avoids overlap with page titles on small screens).
  const isUtilityPage = ['/privacy', '/feedback', '/changelog'].includes(location.pathname);
  // Con un menu aperto (lingua, profilo) la barra resta ferma: prima spariva al click.
  const [menusOpen, setMenusOpen] = useState(0);
  const trackMenu = (open) => setMenusOpen(n => Math.max(0, n + (open ? 1 : -1)));
  const barHidden = useHideOnScroll(!mobileOpen && menusOpen === 0);

  useEffect(() => {
    // Puntino "novità": si aggiorna al cambio pagina e appena la pagina
    // Novità marca tutto come letto (evento da lib/newsMeta.js).
    const refresh = () => setNewsUnread(hasUnreadNews());
    refresh();
    window.addEventListener('scanmarket:news-seen', refresh);
    return () => window.removeEventListener('scanmarket:news-seen', refresh);
  }, [location.pathname]);

  useEffect(() => {
    applyTheme(dark ? 'dark' : 'light');
  }, [dark]);

  // Se l'utente non ha ancora scelto un tema, seguiamo il sistema anche a caldo.
  useEffect(() => watchSystemTheme((theme) => setDark(theme === 'dark')), []);

  useEffect(() => {
    setHeaderSearch(searchQuery || '');
  }, [searchQuery]);

  const handleHeaderSearch = (e) => {
    e.preventDefault();
    if (onSearch && headerSearch.trim()) {
      onSearch(headerSearch.trim());
    }
  };
  const pickSuggestion = (q) => {
    setHeaderSearch(q);
    if (onSearch && q.trim()) onSearch(q.trim());
  };
  const acDesktop = useSearchAutocomplete(headerSearch, pickSuggestion);
  const acMobile = useSearchAutocomplete(headerSearch, pickSuggestion);
  const uiLang = (i18n.language || 'it').split('-')[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('scanmarket_language', code);
    localStorage.setItem('scanmarket_lang_chosen', 'true');
    // If on a language-specific homepage, navigate to the new one
    const homePaths = ['/de', '/fr', '/it', '/en'];
    if (homePaths.includes(location.pathname)) {
      navigate(`/${code}`, { replace: true });
    }
    // If on a valuation page, navigate to the corresponding language path
    const valoreMatch = location.pathname.match(/^\/(de\/wert|fr\/valeur|it\/valore|en\/value)(\/(.+))?$/);
    if (valoreMatch) {
      const slug = valoreMatch[3] || '';
      const valPaths = { de: '/de/wert', fr: '/fr/valeur', it: '/it/valore', en: '/en/value' };
      navigate(`${valPaths[code]}${slug ? '/' + slug : ''}`, { replace: true });
    }
    // Save to user profile if logged in
    if (user) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/lang`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ lang: code }),
      }).catch(() => {});
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const valorePaths = { de: '/de/wert', fr: '/fr/valeur', it: '/it/valore', en: '/en/value' };
  const valorePath = valorePaths[i18n.language?.split('-')[0]] || '/it/valore';

  const navLinks = [
    // "Cerca" (home) hidden when NOT on home — l'input di ricerca al centro dell'header lo sostituisce
    ...(isHome ? [{ to: '/', label: t('home'), icon: <Search size={15} />, group: 'discover' }] : []),
    { to: '/deals', label: t('deals'), icon: <Sparkles size={15} />, group: 'discover' },
    { to: '/newest', label: t('newest'), icon: <Clock size={15} />, group: 'discover' },
    { to: '/auctions', label: t('auctions'), icon: <Hammer size={15} />, group: 'discover' },
    { to: '/saved', label: t('saved'), icon: <Bookmark size={15} />, testid: 'nav-saved-link', group: 'mine' },
    { to: valorePath, label: t('whatIsItWorthShort') || t('whatIsItWorth'), labelFull: t('whatIsItWorth'), icon: <BarChart3 size={15} />, testid: 'nav-valore-link', group: 'discover' },
    // Nascondi "Promuovi" e "Pro" sui viewport medi (tra md e xl) quando NON su home,
    // così lascia spazio all'input di ricerca che altrimenti si comprimerebbe.
    { to: '/featured', label: t('promote'), icon: isHome ? null : <Megaphone size={15} />, highlight: true, hideOnMedium: !isHome, group: 'settings' },
    { to: '/pro', label: isHome ? '💎 Pro' : 'Pro', icon: isHome ? null : <Gem size={15} className="text-sky-500" />, highlight: false, hideOnMedium: !isHome, group: 'settings' },
    // "Novità" compare solo quando c'è qualcosa di nuovo da leggere
    ...(newsUnread
      ? [{ to: '/changelog', label: t('whatsNew'), icon: <Sparkles size={15} />, testid: 'nav-news-link', dot: true, group: 'discover' }]
      : []),
  ];

  return (
    <>
    <div
      data-testid="top-bar"
      data-hidden={barHidden ? 'true' : 'false'}
      className={`sticky top-0 z-50 transition-transform duration-300 ease-out will-change-transform ${barHidden ? '-translate-y-full' : 'translate-y-0'}`}
    >
    <header className="h-14 border-b border-border/70 bg-background/90 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/favicon.png"
            alt="ScanMarket"
            data-testid="header-logo"
            className="w-8 h-8 object-contain"
          />
          {/* Wordmark testuale che rispecchia il logo: Scan rosso + Market.ch grigio */}
          <span className="hidden sm:flex items-baseline gap-0 text-base font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span style={{ color: '#FF0000' }}>Scan</span>
            <span className="text-foreground">Market</span>
            <span className="text-muted-foreground text-sm">.ch</span>
          </span>
        </Link>

        {/* Center search (non-home pages) */}
        {!isHome && (
          <form onSubmit={handleHeaderSearch} className="flex-1 min-w-[240px] max-w-md hidden lg:flex mx-2">
            <div className="relative w-full">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="header-search-input"
                value={headerSearch}
                onChange={e => setHeaderSearch(e.target.value)}
                {...acDesktop.inputProps}
                placeholder={t('searchPlaceholder')}
                className="w-full h-9 pl-9 pr-10 xl:pr-28 text-sm bg-muted/60 border border-border/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-ring/20 focus:bg-background transition-colors"
              />
              <ImageSearchButton className="absolute right-3 top-1/2 -translate-y-1/2 xl:hidden" />
              {/* Live indicator — sempre visibile, discreto */}
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 hidden xl:flex items-center gap-1.5 text-[10px] text-muted-foreground/70 pointer-events-none select-none"
                data-testid="header-live-indicator"
                title={t('liveUpdateBadge')}
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                </span>
                <span>{t('headerLiveText')}</span>
              </span>
              <ImageSearchButton className="absolute right-24 top-1/2 -translate-y-1/2 hidden xl:block" />
              <SearchSuggestions ac={acDesktop} lang={uiLang} testid="header-search" />
            </div>
          </form>
        )}

        {/* Nav Links (desktop) */}
        {/* Fuori dalla home c'è anche la ricerca: le voci diventano solo icona (con tooltip),
            così la barra non va a capo e il profilo non viene tagliato. */}
        <nav className={`hidden lg:flex items-center gap-0.5 xl:gap-1 ${isHome ? '' : 'min-w-0'}`}>
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              title={link.labelFull || link.label}
              aria-label={link.labelFull || link.label}
              className={`flex items-center gap-1 xl:gap-1.5 px-2 xl:px-3 py-1.5 rounded-lg text-sm transition-colors ${isHome ? '' : 'whitespace-nowrap shrink-0'} ${
                link.hideOnMedium ? 'hidden xl:flex' : ''
              } ${
                link.highlight
                  ? 'text-amber-600 hover:bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50'
                  : location.pathname === link.to
                    ? 'bg-foreground text-background font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {link.icon}
              <span className={isHome ? '' : 'sr-only'}>{link.label}</span>
              {link.dot && (
                <span
                  data-testid="news-dot"
                  className="ml-0.5 w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                />
              )}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <CountrySelector />
          {/* Language */}
          <DropdownMenu modal={false} onOpenChange={trackMenu}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="language-switcher" className="gap-1.5 text-xs flex">
                <Globe size={14} />
                {currentLang.code.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              {LANGUAGES.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={i18n.language === lang.code ? 'font-medium' : ''}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Push Notifications — mostra solo se supportato e non ancora attivato */}
          {isSupported && permission !== 'granted' && !isSubscribed && (
            <Button
              variant="ghost"
              size="icon"
              title="Attiva notifiche push"
              onClick={subscribe}
              className="w-8 h-8 text-muted-foreground hover:text-amber-500"
            >
              <Bell size={15} />
            </Button>
          )}
          {isSupported && (permission === 'granted' || isSubscribed) && (
            <Button
              variant="ghost"
              size="icon"
              title="Notifiche push attive"
              className="w-8 h-8 text-amber-500"
            >
              <Bell size={15} className="fill-amber-500/20" />
            </Button>
          )}

          {/* Dark mode */}
          <Button
            variant="ghost"
            size="icon"
            data-testid="theme-toggle"
            onClick={() => { const next = !dark; setDark(next); persistTheme(next ? 'dark' : 'light'); }}
            className="w-8 h-8"
          >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </Button>

          {/* User Auth */}
          {!authLoading && (
            user ? (
              <DropdownMenu modal={false} onOpenChange={trackMenu}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="header-user-menu" className="flex items-center gap-2 rounded-full hover:bg-muted px-2 py-1 transition-colors">
                    <span className="relative shrink-0">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center">
                          <User size={14} />
                        </div>
                      )}
                      {user.is_pro && (
                        <span
                          data-testid="header-pro-badge"
                          title="ScanMarket Pro"
                          className="absolute -top-1.5 -right-1.5 px-1 py-px rounded-full bg-amber-500 text-white text-[8px] font-bold leading-none tracking-wide shadow-sm ring-2 ring-background"
                        >
                          PRO
                        </span>
                      )}
                    </span>
                    <span className="text-xs font-medium hidden sm:block max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-3 py-2 border-b border-border/50">
                    <p className="text-xs font-medium flex items-center gap-1.5">
                      {user.name}
                      {user.is_pro && (
                        <span data-testid="menu-pro-badge" className="px-1.5 py-px rounded-full bg-amber-500 text-white text-[9px] font-bold leading-none">PRO</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => window.location.assign('/assistant')} data-testid="header-assistant-link">
                    <Sparkles size={13} className="mr-2 text-amber-500" />
                    {(() => { const lg = i18n.language?.split('-')[0];
                      return lg === 'de' ? 'Assistent' : lg === 'fr' ? 'Assistant' : lg === 'en' ? 'Assistant' : 'Assistente'; })()}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.location.assign('/pro')} data-testid="header-pro-link">
                    <Crown size={13} className="mr-2 text-amber-500" />
                    {user.is_pro
                      ? (t('proActiveMenu') || 'Pro attivo')
                      : (t('proUpgradeMenu') || 'Passa a Pro')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.location.assign('/settings')} data-testid="header-settings-link">
                    <Settings size={13} className="mr-2" /> {t('settings') || 'Impostazioni'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-500 focus:text-red-500">
                    <LogOut size={13} className="mr-2" /> {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="relative">
                <Button
                  data-testid="header-login-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLoginModal(true)}
                  className="gap-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white border-amber-500 hover:border-amber-600 whitespace-nowrap"
                >
                  <LogIn size={13} />
                  <span className="hidden md:inline">{t('signupOrLogin') || 'Registrati / Accedi'}</span>
                  <span className="md:hidden">{t('login')}</span>
                </Button>
                <SignupNudge onClick={() => setShowLoginModal(true)} />
              </div>
            )
          )}
          {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

          {/* Mobile menu */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden w-8 h-8"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={16} /> : <Menu size={16} />}
          </Button>
        </div>
      </div>

      {/* Mobile menu raggruppato */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-3 space-y-4">
          {['discover', 'mine', 'settings'].map(groupKey => {
            const groupLinks = navLinks.filter(l => l.group === groupKey);
            // Skip empty groups (except 'mine' when there's login button)
            if (groupLinks.length === 0 && !(groupKey === 'mine' && !user)) return null;
            const groupLabelKey = {
              discover: 'menuGroupDiscover',
              mine: 'menuGroupMine',
              settings: 'menuGroupSettings',
            }[groupKey];
            return (
              <div key={groupKey}>
                <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest mb-1.5 px-3">
                  {t(groupLabelKey)}
                </p>
                {groupLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm mb-1 transition-colors ${
                      link.highlight
                        ? 'text-amber-600 hover:bg-amber-500/10'
                        : location.pathname === link.to
                          ? 'bg-foreground text-background font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {link.icon}
                    {link.labelFull || link.label}
                    {link.dot && (
                      <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" data-testid="news-dot-mobile" />
                    )}
                  </Link>
                ))}
                {groupKey === 'mine' && !user && (
                  <button
                    onClick={() => { setMobileOpen(false); setShowLoginModal(true); }}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm mb-1 w-full text-left text-muted-foreground hover:text-foreground hover:bg-muted"
                  >
                    <LogIn size={15} /> {t('login')}
                  </button>
                )}
                {groupKey === 'settings' && (
                  <div className="flex items-center gap-2 mt-2 px-3">
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { changeLanguage(lang.code); setMobileOpen(false); }}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                          i18n.language === lang.code ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground border border-border/60'
                        }`}
                      >
                        {lang.code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </header>

      {/* Mobile search bar — segue il top-bar: sparisce scorrendo giù, ricompare risalendo */}
      {!isHome && !isUtilityPage && (
        <form onSubmit={handleHeaderSearch} className="lg:hidden border-b border-border/50 bg-background/95 backdrop-blur-md px-4 py-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="mobile-header-search-input"
              value={headerSearch}
              onChange={e => setHeaderSearch(e.target.value)}
              {...acMobile.inputProps}
              placeholder={t('searchPlaceholder')}
              className="w-full h-9 pl-9 pr-11 text-sm bg-muted/60 border border-border/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-ring/20 focus:bg-background transition-colors"
            />
            <ImageSearchButton className="absolute right-3 top-1/2 -translate-y-1/2" size={14} />
            <SearchSuggestions ac={acMobile} lang={uiLang} testid="mobile-header-search" />
          </div>
        </form>
      )}
    </div>
    </>
  );
}
